import { useMemo, useState } from 'react';
import Big from 'big.js';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { FieldError } from '../../../../../components/novarium/NvTableParts';
import { OrderAmounts, OrderRow } from '../../../types/billing.types';
import { toBig } from '../billingUtils';

interface AmountsDialogProps {
  row: OrderRow;
  canViewCoverageAmount: boolean;
  onCancel: () => void;
  /** Devuelve el mensaje de error si el back no aceptó los importes; null si se guardaron. */
  onSave: (amounts: OrderAmounts, observations: string | null) => Promise<string | null>;
  /** "Restaurar valores originales": vuelve a los importes de la valorización (sin convenio, $ 0). */
  onRestore: () => void;
}

interface DifferentialValues { financier: number | null; patient: number | null }

const NO_PROFESSIONAL = -1;

/**
 * "Modificar importes" de una fila, como {@code OrderDetailOutOfContractWindow}: importe OS (si el perfil lo puede
 * ver), importe paciente, coseguro, diferencial por profesional y observaciones. Si el convenio resta el coseguro del
 * importe OS, cambiar el coseguro ajusta el importe OS por la diferencia.
 */
export default function AmountsDialog({ row, canViewCoverageAmount, onCancel, onSave, onRestore }: AmountsDialogProps) {
  const initial = row.amounts;
  const [financier, setFinancier] = useState<number | null>(initial?.financierAmount ?? 0);
  const [patient, setPatient] = useState<number | null>(initial?.patientAmount ?? 0);
  const [copayment, setCopayment] = useState<number | null>(initial?.copaymentAmount ?? 0);
  const [observations, setObservations] = useState(row.observations ?? '');
  const [differentials, setDifferentials] = useState<Record<number, DifferentialValues>>(() => {
    const result: Record<number, DifferentialValues> = {};
    for (const d of initial?.differentials ?? []) {
      result[d.professionalId ?? NO_PROFESSIONAL] = { financier: d.financierAmount, patient: d.patientAmount };
    }
    return result;
  });
  const professionals = useMemo(() => (initial?.differentials ?? []).map((d) => ({
    id: d.professionalId ?? NO_PROFESSIONAL,
    name: d.professionalName ?? 'Sin profesional',
  })), [initial]);
  const [professionalId, setProfessionalId] = useState<number>(professionals[0]?.id ?? NO_PROFESSIONAL);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const current = differentials[professionalId] ?? { financier: null, patient: null };
  const setDifferential = (field: keyof DifferentialValues, value: number | null) => {
    setDifferentials((all) => ({ ...all, [professionalId]: { ...(all[professionalId] ?? { financier: null, patient: null }), [field]: value } }));
    setError(null);
  };

  /** Como la ventana de Vaadin: el coseguro que resta del importe OS lo ajusta por la diferencia, sin bajar de 0. */
  const changeCopayment = (value: number | null) => {
    if (row.copaymentSubtracts && canViewCoverageAmount && financier != null && financier > 0) {
      const delta = toBig(value).minus(toBig(copayment));
      const adjusted = toBig(financier).minus(delta);
      setFinancier(adjusted.lt(0) ? 0 : adjusted.toNumber());
    }
    setCopayment(value);
    setError(null);
  };

  async function handleSave() {
    const amounts: Big[] = [toBig(patient), toBig(row.copaymentEditable ? copayment : 0)];
    if (canViewCoverageAmount) amounts.push(toBig(financier));
    for (const d of Object.values(differentials)) {
      amounts.push(toBig(d.patient));
      if (canViewCoverageAmount) amounts.push(toBig(d.financier));
    }
    if (!amounts.some((amount) => amount.gt(0))) {
      setError('Ingresá al menos un importe mayor a 0');
      return;
    }
    setSaving(true);
    const message = await onSave({
      financierAmount: canViewCoverageAmount ? financier ?? 0 : null,
      patientAmount: patient ?? 0,
      copaymentAmount: row.copaymentEditable ? copayment ?? 0 : null,
      differentials: Object.entries(differentials).map(([id, values]) => ({
        professionalId: Number(id) === NO_PROFESSIONAL ? null : Number(id),
        financierAmount: canViewCoverageAmount ? values.financier : null,
        patientAmount: values.patient,
      })),
    }, observations.trim() || null);
    setSaving(false);
    if (message) setError(message);
  }

  const money = (id: string, label: string, value: number | null, onChange: (v: number | null) => void) => (
    <div className="nv-field nv-w-s">
      <label htmlFor={id}>{label}</label>
      <InputNumber
        inputId={id}
        value={value}
        onValueChange={(e) => onChange(e.value ?? null)}
        mode="currency"
        currency="ARS"
        locale="es-AR"
        minFractionDigits={2}
        maxFractionDigits={2}
        min={0}
        max={99999999}
        className={error ? 'p-invalid' : undefined}
      />
    </div>
  );

  return (
    <Dialog
      header="Modificar importes"
      visible
      onHide={onCancel}
      className="nv-scope nv-dialog"
      modal
      draggable={false}
      footer={(
        <>
          {row.modified && (
            <Button type="button" link label="Restaurar valores originales" className="nv-push-left" onClick={onRestore} disabled={saving} />
          )}
          <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={onCancel} disabled={saving} />
          <Button type="button" className="buttonMain" label="Guardar cambios" onClick={handleSave} loading={saving} />
        </>
      )}
    >
      <div className="nv-dialog-body">
        <p className="nv-help">{[row.code, row.description].filter(Boolean).join(' - ')}</p>
        {row.authorizationError && (
          <p className="nv-note"><i className="pi pi-info-circle" aria-hidden /> {row.authorization || 'Convenio no encontrado'}: podés cargar los importes a mano.</p>
        )}
        <div className="nv-form-row">
          {canViewCoverageAmount && money('amounts-financier', 'Importe OS', financier, (v) => { setFinancier(v); setError(null); })}
          {money('amounts-patient', 'Importe paciente', patient, (v) => { setPatient(v); setError(null); })}
          {row.copaymentEditable && money('amounts-copayment', 'Coseguro', copayment, changeCopayment)}
        </div>
        <FieldError id="amounts-error" message={error ?? undefined} />

        {row.copaymentEditable && professionals.length > 0 && (
          <section className="nv-section" aria-labelledby="amounts-differential-title">
            <h3 id="amounts-differential-title" className="nv-section-title">Diferencial</h3>
            <div className="nv-form-row">
              <div className="nv-field nv-w-m">
                <label htmlFor="amounts-professional">Profesional</label>
                <Dropdown panelClassName="nv-scope"
                  inputId="amounts-professional"
                  value={professionalId}
                  options={professionals}
                  optionLabel="name"
                  optionValue="id"
                  disabled={professionals.length <= 1}
                  onChange={(e) => setProfessionalId(e.value)}
                />
              </div>
              {canViewCoverageAmount && money('amounts-diff-financier', 'Importe OS', current.financier, (v) => setDifferential('financier', v))}
              {money('amounts-diff-patient', 'Importe paciente', current.patient, (v) => setDifferential('patient', v))}
            </div>
          </section>
        )}

        <div className="nv-field nv-w-l">
          <label htmlFor="amounts-observations">Observaciones</label>
          <InputTextarea
            id="amounts-observations"
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            rows={3}
            maxLength={255}
            autoResize={false}
          />
        </div>
      </div>
    </Dialog>
  );
}
